import { useMemo, useEffect, useRef, useCallback, ReactNode } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { InputText } from 'primereact/inputtext';
import { Tag } from 'primereact/tag';
import { AgendaAppointment, AgendaViewMode } from '../types/agenda.types';
import { EAppointmentStateValues, EAppointmentState } from '../../../enums/EAppointmentState';
import LiveTimer from './shared/LiveTimer';
import { formatTime, getTimeDiffFormatted } from '../utils/formatUtils';
import { getStatusInfo } from '../utils/statusUtils';
import './ListView.scss';

interface ListViewProps {
  appointments: AgendaAppointment[];
  loading: boolean;
  searchText?: string;
  onSearchChange?: (value: string) => void;
  stateFilter?: number[];
  onStateFilterChange?: (value: number[]) => void;
  coverageFilter?: string | null;
  onCoverageFilterChange?: (value: string | null) => void;
  professionalFilter?: string | null;
  onProfessionalFilterChange?: (value: string | null) => void;
  onRegisterArrival: (id: number) => void;
  onCallPatient: (id: number, appointment: AgendaAppointment) => void;
  onFinishAttention: (id: number, appointment: AgendaAppointment) => void;
  onOpenDetail?: (appointment: AgendaAppointment) => void;
  viewMode?: AgendaViewMode;
  onLoadMore?: () => void;
  hasMore?: boolean;
  loadingMore?: boolean;
  onClearFilters?: () => void;
}

const STATUS_OPTIONS = [
  { label: 'Agendado', value: EAppointmentStateValues[EAppointmentState.SCHEDULLED] },
  { label: 'Confirmado', value: EAppointmentStateValues[EAppointmentState.CONFIRMED] },
  { label: 'En espera', value: EAppointmentStateValues[EAppointmentState.INQUEUE] },
  { label: 'En atención', value: EAppointmentStateValues[EAppointmentState.INATTENTION] },
  { label: 'Atendido', value: EAppointmentStateValues[EAppointmentState.COMPLETED] },
  { label: 'Ausente', value: EAppointmentStateValues[EAppointmentState.MISSED] },
  { label: 'Cancelado', value: EAppointmentStateValues[EAppointmentState.CANCELLED_BY_PATIENT] },
];

const isWaiting = (stateId: number | null) =>
  stateId === EAppointmentStateValues[EAppointmentState.INQUEUE]
  || stateId === EAppointmentStateValues[EAppointmentState.PENDING_ATTENTION];

const isInAttention = (stateId: number | null) =>
  stateId === EAppointmentStateValues[EAppointmentState.INATTENTION]
  || stateId === EAppointmentStateValues[EAppointmentState.IN_OBSERVATION];

const appointmentTime = (row: AgendaAppointment) =>
  row.appointmentfromtime ? row.appointmentfromtime.substring(0, 5) : formatTime(row.appointmentdatetime);

export default function ListView({
  appointments,
  loading,
  searchText = '',
  onSearchChange,
  stateFilter,
  onStateFilterChange,
  coverageFilter,
  onCoverageFilterChange,
  onRegisterArrival,
  onCallPatient,
  onFinishAttention,
  onOpenDetail,
  viewMode = 'professional',
  onLoadMore,
  hasMore = false,
  loadingMore = false,
  onClearFilters,
}: ListViewProps) {
  const coverageOptions = useMemo(() => {
    const names = new Set<string>();
    for (const a of appointments) {
      if (a.businessname) names.add(a.businessname);
    }
    return Array.from(names).sort().map((n) => ({ label: n, value: n }));
  }, [appointments]);

  const sortedAppointments = useMemo(() => {
    return [...appointments].sort((a, b) => {
      const timeA = a.appointmentfromtime || a.appointmentdatetime || '';
      const timeB = b.appointmentfromtime || b.appointmentdatetime || '';
      return timeA.localeCompare(timeB);
    });
  }, [appointments]);

  const tableRef = useRef<DataTable<AgendaAppointment[]>>(null);
  const scrolledRef = useRef(false);

  useEffect(() => {
    if (loading || sortedAppointments.length === 0) return;
    if (scrolledRef.current) return;
    scrolledRef.current = true;

    let targetIdx = sortedAppointments.findIndex((a) => isInAttention(a.appointmentstateid));

    if (targetIdx < 0) {
      targetIdx = sortedAppointments.findIndex((a) => isWaiting(a.appointmentstateid));
    }

    if (targetIdx < 0) {
      const now = new Date();
      const nowMinutes = now.getHours() * 60 + now.getMinutes();
      let bestDiff = Infinity;
      sortedAppointments.forEach((a, idx) => {
        const time = a.appointmentfromtime || '';
        const parts = time.split(':');
        if (parts.length >= 2) {
          const mins = parseInt(parts[0], 10) * 60 + parseInt(parts[1], 10);
          const diff = Math.abs(mins - nowMinutes);
          if (diff < bestDiff) {
            bestDiff = diff;
            targetIdx = idx;
          }
        }
      });
    }

    if (targetIdx > 0) {
      requestAnimationFrame(() => {
        const el = tableRef.current?.getElement();
        const scrollBody = el?.querySelector('.p-datatable-wrapper');
        const rows = el?.querySelectorAll('.p-datatable-tbody > tr');
        if (scrollBody && rows && rows[targetIdx]) {
          const row = rows[targetIdx] as HTMLElement;
          scrollBody.scrollTop = row.offsetTop - scrollBody.clientHeight / 3;
        }
      });
    }
  }, [loading, sortedAppointments]);

  useEffect(() => {
    scrolledRef.current = false;
  }, [appointments.length]);

  // ─── Infinite scroll ─────────────────────────
  const handleScroll = useCallback(() => {
    if (!hasMore || loadingMore || !onLoadMore) return;
    const el = tableRef.current?.getElement();
    const scrollBody = el?.querySelector('.p-datatable-wrapper');
    if (!scrollBody) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollBody;
    if (scrollHeight - scrollTop - clientHeight < 150) {
      onLoadMore();
    }
  }, [hasMore, loadingMore, onLoadMore]);

  useEffect(() => {
    const el = tableRef.current?.getElement();
    const scrollBody = el?.querySelector('.p-datatable-wrapper');
    if (!scrollBody) return;
    scrollBody.addEventListener('scroll', handleScroll);
    return () => scrollBody.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // ─── Clear all filters ────────────────────────
  const handleClearFilters = () => {
    if (onClearFilters) {
      onClearFilters();
    } else {
      onSearchChange?.('');
      onStateFilterChange?.(stateFilter ?? []);
      onCoverageFilterChange?.(null);
    }
  };

  const hasFilters = !!searchText || !!coverageFilter;
  const emptyMessage = hasFilters
    ? <>No hay resultados para estos filtros. <Button type="button" link label="Limpiar filtros" onClick={handleClearFilters} /></>
    : 'Todavía no hay turnos cargados.';

  // ─── Column templates ──────────────────────────

  const hourTemplate = (row: AgendaAppointment) => (
    <div className="list-cell">
      <span className="nv-strong list-cell__num">{appointmentTime(row) || '—'}</span>
      {row.outofagenda && <Tag className="nv-tag-neutral" value="Fuera de agenda" />}
      {!row.outofagenda && row.outofschedule && <Tag className="nv-tag-selected" value="Sobreturno" />}
    </div>
  );

  const priorityTag = (row: AgendaAppointment) =>
    !!row.patientappointmentpriority && <Tag className="nv-tag-prio" value={row.appointmentpriority || 'Prioritario'} />;

  const patientTemplate = (row: AgendaAppointment) => {
    const doc = [row.docshortname, row.document].filter(Boolean).join(' ');
    const sub = [doc, row.clinichistoryid && `HC ${row.clinichistoryid}`].filter(Boolean).join(' · ');
    return (
      <div className="list-cell">
        <div className="nv-row-8">
          <span className="nv-strong">{row.patientname || '—'}</span>
          {priorityTag(row)}
        </div>
        {sub && <span className="nv-help">{sub}</span>}
      </div>
    );
  };

  // Hora de llegada y la acción que corresponde al estado; null si no hay nada que mostrar
  const presentContent = (row: AgendaAppointment): ReactNode => {
    const stateId = row.appointmentstateid;
    if (!stateId) return null;
    const arrival = row.arrivaltime && <span className="list-cell__num">{formatTime(row.arrivaltime)}</span>;

    // En espera → hora de llegada + "Llamar"
    if (row.arrivaltime && isWaiting(stateId)) {
      return (
        <div className="list-cell list-cell--inline">
          {arrival}
          <Button
            type="button"
            label="Llamar"
            className="buttonMain3"
            onClick={() => row.patientcarequeue && onCallPatient(row.patientcarequeue, row)}
          />
        </div>
      );
    }

    // En atención → hora de llegada + "Finalizar"
    if (row.arrivaltime && isInAttention(stateId)) {
      return (
        <div className="list-cell list-cell--inline">
          {arrival}
          <Button
            type="button"
            label="Finalizar"
            className="buttonMain3"
            onClick={() => row.patientcarequeue && onFinishAttention(row.patientcarequeue, row)}
          />
        </div>
      );
    }

    // Ausente → hora de llegada (si hay) + "Reprogramar"
    if (stateId === EAppointmentStateValues[EAppointmentState.MISSED]) {
      return (
        <div className="list-cell list-cell--inline">
          {arrival}
          <Button type="button" label="Reprogramar" className="buttonMain3" onClick={() => onOpenDetail?.(row)} />
        </div>
      );
    }

    // Ya llegó (otros estados) → solo la hora de llegada
    if (row.arrivaltime) return arrival;

    // Agendado o confirmado → "Registrar llegada"
    if (
      stateId === EAppointmentStateValues[EAppointmentState.SCHEDULLED] ||
      stateId === EAppointmentStateValues[EAppointmentState.CONFIRMED]
    ) {
      return (
        <Button
          type="button"
          label="Registrar llegada"
          className="buttonMain3"
          onClick={() => row.patientcarequeue && onRegisterArrival(row.patientcarequeue)}
        />
      );
    }

    return null;
  };

  // Duración de la espera o de la atención (en vivo), o de la atención terminada
  const durationTemplate = (row: AgendaAppointment) => {
    const stateId = row.appointmentstateid;
    let duration: ReactNode = null;
    if (isWaiting(stateId) && row.arrivaltime) {
      duration = <LiveTimer since={row.arrivaltime} />;
    } else if (isInAttention(stateId) && row.servedtime) {
      duration = <LiveTimer since={row.servedtime} />;
    } else if (stateId === EAppointmentStateValues[EAppointmentState.COMPLETED] && row.servedtime && row.completedtime) {
      duration = getTimeDiffFormatted(row.servedtime, row.completedtime);
    }
    return duration && <span className="nv-help list-cell__num">{duration}</span>;
  };

  const statusTemplate = (row: AgendaAppointment) => {
    const { label, tagClass } = getStatusInfo(row.appointmentstateid);
    if (!label) return '—';
    return (
      <div className="list-cell">
        <Tag className={tagClass} value={label} />
        {durationTemplate(row)}
      </div>
    );
  };

  const coverageTemplate = (row: AgendaAppointment) => {
    const entity = [row.businessname, row.planname].filter(Boolean).join(' · ');
    if (!entity && !row.benefitnumber) return '—';
    return (
      <div className="list-cell">
        <span>{entity || '—'}</span>
        {row.benefitnumber && <span className="nv-help">Af. {row.benefitnumber}</span>}
      </div>
    );
  };

  const billedTemplate = (row: AgendaAppointment) => (row.hasbenefitorder ? 'Sí' : 'No');

  const professionalTemplate = (row: AgendaAppointment) => {
    const name = viewMode === 'service' ? row.medicalspeciality : row.professional;
    const sub = viewMode === 'service' ? row.professional : (row.videocall ? 'Videollamada' : 'Consultorio');
    return (
      <div className="list-cell">
        <span>{name || '—'}</span>
        {sub && <span className="nv-help">{sub}</span>}
      </div>
    );
  };

  const notesTemplate = (row: AgendaAppointment) => {
    const text = row.reasonconsultation || row.notes || '';
    return text ? <span className="nv-truncate nv-tip list-cell__note" data-pr-tooltip={text}>{text}</span> : '—';
  };

  const detailButton = (row: AgendaAppointment) => (
    <Button
      type="button"
      text
      className="nv-icon nv-tip"
      icon="pi pi-eye"
      aria-label="Ver detalle"
      data-pr-tooltip="Ver detalle"
      onClick={() => onOpenDetail?.(row)}
    />
  );

  // ─── Filter templates ──────────────────────
  const clearFiltersElement = () => (
    <Button
      type="button"
      rounded
      className="nv-control nv-tip"
      icon="pi pi-eraser"
      aria-label="Limpiar filtros"
      data-pr-tooltip="Limpiar filtros"
      onClick={handleClearFilters}
    />
  );

  const patientSearchFilter = () => (
    <span className="p-input-icon-left">
      <i className="pi pi-search" aria-hidden />
      <InputText
        value={searchText}
        onChange={(e) => onSearchChange?.(e.target.value)}
        placeholder="Buscar paciente"
        aria-label="Buscar paciente"
      />
    </span>
  );

  const stateFilterElement = () => (
    <MultiSelect
      value={stateFilter}
      options={STATUS_OPTIONS}
      onChange={(e) => onStateFilterChange?.(e.value)}
      placeholder="Todos"
      display="comma"
      maxSelectedLabels={1}
      selectedItemsLabel="{0} estados"
      aria-label="Filtrar por estado"
      panelClassName="nv-scope"
    />
  );

  const coverageFilterElement = () => (
    <Dropdown
      value={coverageFilter}
      options={coverageOptions}
      onChange={(e) => onCoverageFilterChange?.(e.value)}
      placeholder="Todas"
      showClear
      emptyMessage="Sin resultados"
      aria-label="Filtrar por cobertura"
      panelClassName="nv-scope"
    />
  );

  const applied = (active: boolean) => (active ? 'nv-filter-applied' : undefined);

  return (
    <div className="agenda-list-view">
      {/* Desktop: DataTable */}
      <div className="agenda-list-view__desktop">
        <DataTable
          ref={tableRef}
          value={sortedAppointments}
          loading={loading}
          scrollable
          scrollHeight="calc(100vh - 420px)"
          tableStyle={{ minWidth: '1100px' }}
          className="nv-multiline"
          emptyMessage={emptyMessage}
          filterDisplay="row"
        >
          <Column body={hourTemplate} header="Hora" align="center" style={{ width: '128px' }} />
          <Column
            body={patientTemplate}
            header="Paciente"
            style={{ minWidth: '180px' }}
            filter
            filterField="patientname"
            showFilterMenu={false}
            filterElement={patientSearchFilter}
            filterHeaderClassName={applied(!!searchText)}
          />
          <Column body={(row: AgendaAppointment) => presentContent(row) ?? '—'} header="Presente" style={{ width: '200px' }} />
          <Column
            body={statusTemplate}
            header="Estado"
            style={{ width: '160px' }}
            filter
            filterField="appointmentstateid"
            showFilterMenu={false}
            filterElement={stateFilterElement}
            filterHeaderClassName={applied(!!stateFilter?.length)}
          />
          <Column
            body={coverageTemplate}
            header="Cobertura"
            style={{ minWidth: '140px' }}
            filter
            filterField="businessname"
            showFilterMenu={false}
            filterElement={coverageFilterElement}
            filterHeaderClassName={applied(!!coverageFilter)}
          />
          <Column body={billedTemplate} header="Facturado" align="center" style={{ width: '96px' }} />
          <Column
            body={professionalTemplate}
            header={viewMode === 'service' ? 'Especialidad' : 'Profesional / Agenda'}
            style={{ minWidth: '150px' }}
          />
          <Column body={notesTemplate} header="Sesión / Nota" style={{ minWidth: '120px' }} />
          <Column
            body={detailButton}
            header={<span className="nv-sr-only">Acciones</span>}
            className="nv-col-actions"
            align="right"
            alignHeader="right"
            filter
            filterField="_actions"
            showFilterMenu={false}
            filterElement={clearFiltersElement}
          />
        </DataTable>
        {loadingMore && (
          <div className="nv-loading list-view__loading-more" role="status">
            <i className="pi pi-spinner pi-spin" aria-hidden /> Cargando más turnos…
          </div>
        )}
      </div>

      {/* Mobile: Card list */}
      <div className="agenda-list-view__mobile">
        {loading && (
          <div className="nv-loading mobile-card-message" role="status">
            <i className="pi pi-spinner pi-spin" aria-hidden /> Cargando…
          </div>
        )}
        {!loading && sortedAppointments.length === 0 && (
          <div className="nv-muted mobile-card-message">{emptyMessage}</div>
        )}
        {!loading && sortedAppointments.map((row) => {
          const { label: statusLabel, tagClass, cssClass } = getStatusInfo(row.appointmentstateid);

          return (
            <div key={row.patientcarequeue ?? row.appointmentdatetime} className={cssClass ? `mobile-card mobile-card--${cssClass}` : 'mobile-card'}>
              <div className="mobile-card__header">
                <span className="nv-strong list-cell__num">{appointmentTime(row) || '—'}</span>
                {row.outofagenda && <Tag className="nv-tag-neutral" value="Fuera de agenda" />}
                {!row.outofagenda && row.outofschedule && <Tag className="nv-tag-selected" value="Sobreturno" />}
                {priorityTag(row)}
                {statusLabel && <Tag className={`${tagClass} mobile-card__status`} value={statusLabel} />}
              </div>
              <div className="nv-strong mobile-card__patient">{row.patientname || '—'}</div>
              <div className="mobile-card__footer">
                <div className="mobile-card__action">
                  {presentContent(row)}
                </div>
                {detailButton(row)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
